<template>
  <div class="controllerTab">
    <BoxWithTitle
      collapsible
      openingIcon="mdi-chevron-down"
      closingIcon="mdi-chevron-up"
    >
      <template #header>
        <v-toolbar-title>
          {{ $t('devices.controllersHubs.name') }}
        </v-toolbar-title>
      </template>
      <template #content>
        <v-skeleton-loader
          v-if="loading"
          class="m-auto"
          type="table"
          transition="scale-transition"
        />
        <VTableRemote
          :items="displayedControllers"
          :headers="tableHeaders"
          :sortableColumns="sortableColumns"
          :itemActions="itemActions"
          :disablePagination="true"
          :pagination="pagination"
          :iconHeader="'mdi-plus'"
          hideDefaultFooter
          :isCustomIcon="true"
          :isInfinitScroll="true"
          :isSearchable="false"
          :title="$t('devices.controllersHubs.name')"
          :centerColumns="['action']"
          :notSortableColumns="['action']"
          startIconName="cell_tower"
          showExpand
          @iconAction="onClickAction"
          @sendQueryParams="fetchQuery"
        >
          <template v-slot:item.testCSV="{ item }">
            <div
              style="display: flex; flex-direction: column; align-items: center;"
            >
              <v-btn
                color="secondary"
                class="mb-2"
                @click="() => testCSVForm(item.controllerId, item.probes, item.dateFormat)"
              >
                {{ $t('controller.testCSV') }}
              </v-btn>
            </div>
          </template>

          <template #SearchWithFilters>
            <SearchWithFilters
              :filters="[]"
              :searchPlaceholder="$t('controls.search')"
              :filterName="filterName"
              @onSearch="handleSearch"
            />
          </template>
          <template #expanded-item="props">
            <td :colspan="props.headers.length">
              <ControllerDetails
                :controllerId="props.item.id"
                :probes="props.item.probes"
                @reloadController="reload"
              />
            </td>
          </template>
          <template #withInfiniteScroll>
            <div v-intersect="endIntersect" />
          </template>
        </VTableRemote>
      </template>
    </BoxWithTitle>
  </div>
</template>

<script>
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import Vue from 'vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import { mapGetters } from 'vuex';
import perpage from '@/constants/pagination';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import { CONNECTION_TYPES } from '@/aquassist-shared/constants/types/deviceConnection';
import ControllerDetails from './probe/ControllerDetails.vue';
import ControllerForm from './controllersHub/ControllerForm.vue';
import ProbeForm from './probe/ProbesForm.vue';
import TestCSVForm from './testCSV/TestCSVForm.vue';

export default {
    name: 'ControllerTab',
    components: {
        VTableRemote,
        ControllerDetails,
        BoxWithTitle,
        SearchWithFilters,
    },
    data() {
        return {
            loading: false,
            controllers: [],
            filterName: 'controllers',
            queryParams: { page: 1, perpage },
            pagination: {},
        };
    },
    computed: {
        ...mapGetters([
            'site',
            'hasPermission',
            'hasRole',
        ]),
        hasSiteId() {
            return Boolean(this.site.id);
        },
        tableHeaders() {
            const headers = [
                {
                    text: this.$t('devices.name'),
                    value: 'name',
                    sortable: false,
                },
                {
                    text: this.$t('devices.make'),
                    value: 'make',
                    sortable: false,
                },
                {
                    text: this.$t('devices.model'),
                    value: 'model',
                    sortable: false,
                },
                {
                    text: this.$t('devices.IDNumber'),
                    value: 'controllerId',
                    sortable: false,
                },
                {
                    text: this.$t('devices.connectionType'),
                    value: 'translatedConnectionType',
                    sortable: false,
                },
                {
                    text: this.$t('forms.labels.dateFormat'),
                    value: 'dateFormat',
                    sortable: false,
                },
                {
                    text: '',
                    value: 'testCSV',
                    sortable: false,
                },
            ];
            headers.unshift({
                text: '',
                value: 'icon',
                sortable: false,
            });

            return headers;
        },
        sortableColumns() {
            return this.tableHeaders
                .filter(column => column.sortable)
                .map(column => this.$options.filters.lowercase(column.value));
        },
        displayedControllers() {
            return this.controllers.map(hub => {
                const translatedConnectionType = this.translateConnectionType(hub.connectionType);
                return { ...hub, translatedConnectionType };
            });
        },

        itemActions() {
            const actions = [];
            if (this.hasPermission('controllers.edit')) {
                actions.push({ name: this.$t('controller.editController'), icon: 'edit' });
            }
            if (this.hasPermission('controllers.edit')) {
                actions.push({ name: this.$t('devices.probes.add'), icon: 'mdi-plus' });
            }
            if (this.hasPermission('controllers.remove')) {
                actions.push({ name: this.$t('forms.labels.delete'), icon: 'delete', iconAndTextColor: '#EF4135' });
            }
            return actions;
        },

    },

    async created() {
        this.loading = true;
        await this.getControllers();
        this.loading = false;
    },

    methods: {
        translateConnectionType(connectionType) {
            return CONNECTION_TYPES.includes(connectionType) ? this.$t(`devices.controllersHubs.connectionType.${connectionType}`) : connectionType;
        },
        async fetchQuery(queryParams) {
            this.queryParams = {
                page: 1,
                perpage: queryParams.perpage,
                search: queryParams.search,
            };
            this.pagination = {};
            this.controllers = [];
            await this.getControllers();
        },
        async onClickAction(item, action) {
            if (item === 'mdi-plus') { // TODO WHY??
                await this.addController();
            }
            if (action) {
                switch (action?.icon) {
                    case 'delete':
                        await this.deleteController(item);
                        break;
                    case 'edit':
                        await this.editController(item);
                        break;
                    case 'mdi-plus':
                        await this.editProbeModal(item);
                        break;
                    default:
                        break;
                }
            }
        },
        async reload() {
            this.pagination = {};
            this.controllers = [];
            this.queryParams.page = 1;
            await this.getControllers();
        },
        handleSearch(search) {
            this.fetchQuery({ search });
        },
        async getControllers() {
            if (this.hasSiteId) {
                // fetch for the first time OR fetch next page
                if (!this.pagination?.currentPage || (this.pagination?.currentPage !== this.queryParams?.page)) {
                    const { data } = await this.$rq.getControllers(this.site.id, this.queryParams);
                    this.controllers = [...this.controllers, ...data.controllers];
                    this.pagination = data.pagination;
                }
            }
        },
        async deleteController(controller) {
            this.$root.$emit('showModal', {
                title: this.$t('controller.remove'),
                component: Vue.component('confirmRemove', ConfirmForm),
                payload: { msg: this.$t('controller.removeMsg') },
                onConfirm: async () => {
                    await this.$rq.deleteController(this.site.id, controller.id);
                    await this.reload();
                },
                isForm: false,
            });
        },
        async editController(controller) {
            this.$root.$emit('showModal', {
                title: this.$t('controller.editController'),
                component: Vue.component('controllerForm', ControllerForm),
                payload: { controller },
                onConfirm: async requestPayload => {
                    if (!requestPayload) return;
                    await this.$rq.editControllerDetails(this.site.id, controller.id, requestPayload);
                    await this.reload();
                },
                isForm: false,
                width: 800,
            });
        },
        async addController() {
            this.$root.$emit('showModal', {
                title: this.$t('controller.addController'),
                component: Vue.component('controllerForm', ControllerForm),
                payload: { },
                onConfirm: requestPayload => this.onConfirmAdd(requestPayload),
                width: 800,
            });
        },
        async onConfirmAdd(requestPayload) {
            await this.$rq.addController(this.site.id, requestPayload);
            await this.reload();
        },

        async editProbeModal(controller) {
            this.$root.$emit('showModal', {
                title: this.$t('controller.probe.edit'),
                component: Vue.component('ProbeForm', ProbeForm),
                payload: { editMode: true, controller },
                onConfirm: async requestPayload => {
                    const payload = {
                        probes: requestPayload.map(probe => ({
                            ...probe,
                            analysisType: probe.analysisType?.id,
                            component: probe.component?.id,
                        })),
                    };
                    await this.$rq.editController(this.site.id, controller.id, payload);
                    await this.reload();
                },
                controlledValidation: true,
                isForm: true,
                width: 800,
            });
        },

        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getControllers();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        testCSVForm(controllerId, probes, dateFormat) {
            const probesIds = probes.map(probe => probe.probeId);

            this.$root.$emit('showModal', {
                title: this.$t('controller.testCSV'),
                showConfirmButton: false,
                component: Vue.component('testCSVForm', TestCSVForm),
                payload: {
                    controllerId,
                    probesIds,
                    dateFormat,
                },
                width: 800,
            });
        },
    },
};
</script>
<style lang="scss">
    .controllerTab td {
        width: 350px
    }

    @media screen and (max-width: 600px) {
        .controllerTab td {
            width: max-content
        }
    }

</style>
